import {
  DEV_TIER_OVERRIDE_HEADER,
  readDevTierOverride,
} from './devTierOverride'

// Default to same-origin relative URLs in production (the SPA and the API
// share a CloudFront domain). Override with VITE_API_URL for local dev or
// when pointing at a non-default environment — required for the Capacitor
// mobile build, since the document origin there is `https://localhost`
// (see `ios.scheme: 'https'` in packages/mobile/capacitor.config.ts) and
// a relative `/api/...` would resolve to that non-existent origin. The
// web login flow (`routes/login.tsx`) also reads this so the WorkOS
// hosted-auth redirect leaves localhost and actually hits the deployed
// API instead of Vite's 404 handler.
export const API_BASE = import.meta.env.VITE_API_URL ?? ''

export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    // Raw response body (parsed JSON when available, or the text
    // response otherwise). Exposed so callers can read structured
    // error fields like `currentVersion` on 409 Version Conflicts
    // without re-parsing the message string.
    public body?: unknown,
  ) {
    super(message)
    this.name = 'APIError'
  }
}

/**
 * Native auth token state. On the web build this stays null and we fall
 * back to cookie auth (HttpOnly session cookies set by WorkOS callback).
 * On the Capacitor-wrapped iOS / iPadOS / macOS build, the native auth
 * bootstrap (`packages/app/src/lib/nativeAuth.ts`) sets these after
 * reading the tokens from Keychain on app launch, and also updates them
 * after the ASWebAuthenticationSession sign-in flow completes.
 *
 * Keeping this as module-level state (rather than a React context) is
 * deliberate — the token has to be readable by the plain `fetch` calls
 * below, and every API call must see the latest value immediately
 * without a re-render.
 */
let authToken: string | null = null
let refreshToken: string | null = null

export function setAuthTokens(access: string, refresh: string) {
  authToken = access
  refreshToken = refresh
}

export function clearAuthTokens() {
  authToken = null
  refreshToken = null
}

export function getAuthToken(): string | null {
  return authToken
}

export function getRefreshToken(): string | null {
  return refreshToken
}

function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`
  }
  // Dev tier override: the Lambda only honors this header when
  // `ALLOW_DEV_TIER_OVERRIDE=true` is set on its environment (dev/staging
  // stacks only). Prod Lambdas leave the env var empty, so a forged header
  // is a no-op server-side regardless of what's in localStorage. Sending
  // on every request keeps the server's auth context in lock-step with
  // the client's `useEffectiveFeatures` without a second round-trip.
  const override = readDevTierOverride()
  if (override) {
    headers[DEV_TIER_OVERRIDE_HEADER] = override
  }
  return headers
}

async function handleResponse(response: Response) {
  const contentType = response.headers.get('content-type')
  let data: any

  if (contentType?.includes('application/json')) {
    data = await response.json()
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    const status = response.status
    const code = data?.code || 'UNKNOWN_ERROR'
    const message = data?.message || data?.error || response.statusText
    throw new APIError(status, code, message, data)
  }

  return data
}

/**
 * Attempt a silent refresh of the access token using the stored refresh
 * token. Native-only — the web build uses an HttpOnly refresh cookie
 * that the backend rotates automatically inside `authenticateRequest`.
 *
 * Returns `true` on success. The caller (`fetchWithRetry`) then retries
 * the original request with the new token attached.
 */
async function tryNativeRefresh(): Promise<boolean> {
  if (!refreshToken) return false
  try {
    const response = await fetch(`${API_BASE}/api/v1/auth/native/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
    })
    if (!response.ok) return false
    const data = (await response.json()) as {
      access?: string
      refresh?: string
    }
    if (!data.access || !data.refresh) return false
    authToken = data.access
    refreshToken = data.refresh
    // Let listeners (the native bootstrap writes to Keychain) persist
    // the rotated tokens.
    nativeRefreshListeners.forEach(fn => {
      try {
        fn(data.access!, data.refresh!)
      } catch {
        /* ignore individual listener errors */
      }
    })
    return true
  } catch {
    return false
  }
}

type NativeRefreshListener = (access: string, refresh: string) => void
const nativeRefreshListeners = new Set<NativeRefreshListener>()

export function onNativeTokensRefreshed(fn: NativeRefreshListener): () => void {
  nativeRefreshListeners.add(fn)
  return () => nativeRefreshListeners.delete(fn)
}

async function fetchWithRetry(
  path: string,
  init: RequestInit,
): Promise<Response> {
  const response = await fetch(`${API_BASE}${path}`, init)
  // Only attempt a native refresh flow when we actually have a refresh
  // token on hand. Web clients hit the cookie refresh path inside the
  // backend and should never see this code.
  if (response.status === 401 && refreshToken) {
    const refreshed = await tryNativeRefresh()
    if (refreshed) {
      const retryInit: RequestInit = {
        ...init,
        headers: buildHeaders(
          (init.headers as Record<string, string> | undefined) ?? undefined,
        ),
      }
      return fetch(`${API_BASE}${path}`, retryInit)
    }
  }
  return response
}

export const apiClient = {
  async get<T = any>(path: string): Promise<T> {
    const response = await fetchWithRetry(path, {
      method: 'GET',
      credentials: 'include',
      headers: buildHeaders(),
    })
    return handleResponse(response)
  },

  async post<T = any>(path: string, body?: any): Promise<T> {
    const response = await fetchWithRetry(path, {
      method: 'POST',
      credentials: 'include',
      headers: buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse(response)
  },

  async put<T = any>(path: string, body?: any): Promise<T> {
    const response = await fetchWithRetry(path, {
      method: 'PUT',
      credentials: 'include',
      headers: buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse(response)
  },

  async patch<T = any>(path: string, body?: any): Promise<T> {
    const response = await fetchWithRetry(path, {
      method: 'PATCH',
      credentials: 'include',
      headers: buildHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    return handleResponse(response)
  },

  async delete<T = any>(path: string): Promise<T> {
    const response = await fetchWithRetry(path, {
      method: 'DELETE',
      credentials: 'include',
      headers: buildHeaders(),
    })
    return handleResponse(response)
  },
}
